import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useTheme } from '@/components/theme/ThemeProvider';
import { useAuth } from '@/lib/store';
import { Copy, Check, Sun, Moon, Monitor } from 'lucide-react';
import { useState } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

export function Settings() {
  const { theme, setTheme } = useTheme();
  const { token } = useAuth();
  const [copied, setCopied] = useState(false);

  const handleCopyToken = async () => {
    if (token) {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-ibm-condensed">Settings</h1>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
            <CardDescription>
              Customize how Pax Intelligence looks on your device.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup 
              defaultValue={theme} 
              onValueChange={(value) => setTheme(value as 'light' | 'dark' | 'system')}
              className="grid gap-4"
            >
              <div>
                <RadioGroupItem value="light" id="light" className="peer sr-only" />
                <Label
                  htmlFor="light"
                  className="flex items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                >
                  <div className="space-y-1">
                    <p className="font-semibold leading-none">Light</p>
                    <p className="text-sm text-muted-foreground">
                      Use light theme
                    </p>
                  </div>
                  <Sun className="h-5 w-5" />
                </Label>
              </div>
              <div>
                <RadioGroupItem value="dark" id="dark" className="peer sr-only" />
                <Label
                  htmlFor="dark"
                  className="flex items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                >
                  <div className="space-y-1">
                    <p className="font-semibold leading-none">Dark</p>
                    <p className="text-sm text-muted-foreground">
                      Use dark theme
                    </p>
                  </div>
                  <Moon className="h-5 w-5" />
                </Label>
              </div>
              <div>
                <RadioGroupItem value="system" id="system" className="peer sr-only" />
                <Label
                  htmlFor="system"
                  className="flex items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                >
                  <div className="space-y-1">
                    <p className="font-semibold leading-none">System</p>
                    <p className="text-sm text-muted-foreground">
                      Match system theme
                    </p>
                  </div>
                  <Monitor className="h-5 w-5" />
                </Label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>API Key</CardTitle>
            <CardDescription>
              Your API key for accessing Pax Intelligence services.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex space-x-2">
              <Input
                value={token || ''}
                readOnly
                className="font-mono"
              />
              <Button 
                variant="outline" 
                onClick={handleCopyToken}
                disabled={!token}
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              This API key grants access to all Pax Intelligence APIs. Keep it secure and never share it publicly.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}